import { createContext, useContext, useState, useEffect } from 'react'
import api, { authAPI } from '../services/api'

const AuthContext = createContext(null)

/**
 * Decode a JWT token payload without any external library.
 * Returns the parsed JSON payload or null if decoding fails.
 */
const decodeToken = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload
  } catch {
    return null
  }
}

/**
 * Check whether a decoded JWT payload is still valid (not expired).
 */
const isTokenValid = (payload) => {
  if (!payload || !payload.exp) return false
  return payload.exp > Date.now() / 1000
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(null)
  const [loading, setLoading] = useState(true)

  // On mount: check localStorage for existing token
  useEffect(() => {
    const storedToken = localStorage.getItem('freight_token')

    if (storedToken) {
      const payload = decodeToken(storedToken)

      if (payload && isTokenValid(payload)) {
        // Set user from decoded JWT claims
        const userData = {
          id: payload.jti || payload.sub || payload.id,
          email: payload.email || '',
          name: payload.name || '',
          role: payload.role || ''
        }
        setUser(userData)
        setToken(storedToken)
        // Configure Authorization header on the api instance
        api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`
      } else {
        // Token expired or invalid — clean up
        localStorage.removeItem('freight_token')
        delete api.defaults.headers.common['Authorization']
      }
    }

    setLoading(false)
  }, [])

  /**
   * Log in with email and password.
   * Stores the JWT, sets the user state, and configures the api header.
   */
  const login = async (email, password) => {
    const response = await authAPI.login(email, password)
    const { token: newToken, user: userData } = response

    localStorage.setItem('freight_token', newToken)
    api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`
    setToken(newToken)
    setUser(userData)

    return response
  }

  /**
   * Log out: clear all auth state and redirect to login.
   */
  const logout = () => {
    localStorage.removeItem('freight_token')
    delete api.defaults.headers.common['Authorization']
    setUser(null)
    setToken(null)
    // Use window.location.href since context may be outside Router
    window.location.href = '/admin/login'
  }

  const isAuthenticated = user !== null && token !== null

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  )
}

/**
 * Custom hook to access auth context.
 * Must be used within an AuthProvider.
 */
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext
